import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { TextField, MenuItem } from '@mui/material'
import { useContext, useEffect, useState } from 'react'

import { Pocket } from '../../utils/dexie/db'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { AppStateContext, UserContext } from '../../contexts'
import { handleGetParsedValue, numberWithCommas } from '../../utils'
import { Customer, BankAccount, Bank, FormError } from '../../types'
import { customersDocumentType, customersBankAccountType, countriesCurrencies } from '../../lib/common.lib'

const CustomerDataForm = ({
  errors,
  banksData,
  amountToSend,
  customerData,
  selectedPocketId,
  handleSetPocketId,
  handleSetCustomerData,
  handleSetPaymentAmountToSend
}: {
  errors: FormError
  amountToSend?: string
  banksData: Array<Bank>
  selectedPocketId?: string
  customerData: Partial<Customer> | null
  handleSetPocketId?: (pocketIdToSet: string) => void
  handleSetCustomerData: (newCustomerData: Customer) => void
  handleSetPaymentAmountToSend?: (paymentAmountToSend: string) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry]

  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const { currentPocketsData, defaultPocketIdSelected } = useContext(AppStateContext)
  const { t } = useTranslation(['customers-management', 'individual-money-transfers-management'])

  const [showBankNameSelect, setShowBankNameSelect] = useState<boolean>(true)

  useEffect(() => {
    if (userSession?.role === 'super-admin-client' && currentPocketsData?.length === 1 && defaultPocketIdSelected) {
      handleSetPocketId?.(defaultPocketIdSelected)
    }

    if ((userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder') && defaultPocketIdSelected) {
      handleSetPocketId?.(defaultPocketIdSelected)
    }

    // if (userSession?.role === 'super-admin-client') {
    //   handleGetCurrentPocketsData?.()
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, currentPocketsData, defaultPocketIdSelected])

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value

    if (input === '') {
      return ''
    }

    const first = input[0]

    if (/[0-9]/.test(first)) {
      if (/^\d+$/.test(input)) {
        return input
      }
    }

    if (first === '@') {
      const pattern = /^@[A-Za-z0-9]*$/
      if (pattern.test(input)) {
        return input
      }
    }

    if (/[A-Za-z]/.test(first)) {
      const allowedChars = /^[A-Za-z0-9._%+\-@]*$/
      if (!allowedChars.test(input)) {
        return
      }

      const atCount = (input.match(/@/g) || []).length
      if (atCount > 1) {
        return
      }

      return input
    }
  }

  return (
    <div className="customers-add-new-form-container">
      <div className="customers-add-new-form-personal-data-container">
        <div className="customers-add-new-form-personal-data-title">{t('newCustomerModal.customerDataForm.title')}</div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('newCustomerModal.customerDataForm.fields.firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              value={customerData?.firstName || ''}
              onChange={(event) => {
                const customerDataToSet = {
                  ...customerData,
                  firstName: event.target.value.toUpperCase()
                }

                handleSetCustomerData(customerDataToSet)
              }}
            />
          </div>
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('newCustomerModal.customerDataForm.fields.lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              value={customerData?.lastName || ''}
              onChange={(event) => {
                const customerDataToSet = {
                  ...customerData,
                  lastName: event.target.value.toUpperCase()
                }

                handleSetCustomerData(customerDataToSet)
              }}
            />
          </div>
        </div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('newCustomerModal.customerDataForm.fields.documentType')}
              error={!!errors.documentType}
              helperText={errors.documentType}
              value={customerData?.documentType || ''}
              onChange={(event) => {
                const customerDataToSet = {
                  ...customerData,
                  documentType: event.target.value
                }

                handleSetCustomerData(customerDataToSet)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newCustomerModal.customerDataForm.fields.select')}
              </MenuItem>
              {Object.keys(customersDocumentType).map((documentType: string) => {
                return (
                  <MenuItem
                    key={documentType}
                    value={documentType}
                  >
                    {(customersDocumentType as any)[documentType].toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={t('newCustomerModal.customerDataForm.fields.documentNumber')}
              error={!!errors.documentNumber}
              helperText={errors.documentNumber}
              value={customerData?.documentNumber || ''}
              onChange={(event) => {
                const customerDataToSet = {
                  ...customerData,
                  documentNumber: event.target.value.toUpperCase()
                }

                handleSetCustomerData(customerDataToSet)
              }}
            />
          </div>
        </div>
        {userSession?.role === 'super-admin-client' && location.pathname !== '/' && (
          <div className="customers-add-new-form-personal-data-form-row">
            <TextField
              select
              fullWidth
              variant="standard"
              value={selectedPocketId || ''}
              error={!!errors.selectedPocketId}
              helperText={errors.selectedPocketId}
              onChange={(event) => handleSetPocketId?.(event.target.value)}
              label={t('newCustomerModal.customerDataForm.fields.assignTo')}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newCustomerModal.customerDataForm.fields.select')}
              </MenuItem>
              <MenuItem value="all">{t('newCustomerModal.customerDataForm.fields.all').toUpperCase()}</MenuItem>
              {currentPocketsData?.map((pocketData: Pocket) => {
                return (
                  <MenuItem
                    key={String(pocketData.id)}
                    value={String(pocketData.id)}
                  >
                    {pocketData.name?.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
      </div>
      <div className="customers-add-new-form-bank-data-container">
        <div className="customers-add-new-form-bank-data-title">{t('newCustomerModal.customerDataForm.subtitle')}</div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('newCustomerModal.customerDataForm.fields.bankAccountType')}
              error={!!errors.bankAccountType}
              helperText={errors.bankAccountType}
              value={Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? customerData?.bankAccountsData[0].bankAccountType : ''}
              onChange={(event) => {
                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...(customerData?.bankAccountsData || [])]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                currentCustomerBankAccountsData[0].bankAccountType = event.target.value

                if (event.target.value === 'breb') {
                  setShowBankNameSelect(false)
                } else {
                  setShowBankNameSelect(true)
                }

                const customerDataToSet = {
                  ...customerData,
                  bankAccountsData: [currentCustomerBankAccountsData[0]]
                }

                handleSetCustomerData(customerDataToSet)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newCustomerModal.customerDataForm.fields.select')}
              </MenuItem>
              {Object.keys(customersBankAccountType).map((bankAccountType: string) => {
                return (
                  <MenuItem
                    key={bankAccountType}
                    value={bankAccountType}
                  >
                    {(customersBankAccountType as any)[bankAccountType].toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
          {showBankNameSelect && (
            <div className="customers-add-new-form-personal-data-form-field-container">
              <TextField
                select
                fullWidth
                variant="standard"
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                inputProps={{
                  style: { textAlign: 'center' }
                }}
                label={t('newCustomerModal.customerDataForm.fields.bankName')}
                error={!!errors.bankName}
                helperText={errors.bankName}
                value={
                  customerData?.bankAccountsData &&
                  Array.isArray(customerData?.bankAccountsData) &&
                  customerData?.bankAccountsData.length > 0 &&
                  customerData?.bankAccountsData[0].bankName &&
                  customerData?.bankAccountsData[0].bankAchCode
                    ? `${customerData?.bankAccountsData[0].bankName}-${customerData?.bankAccountsData[0].bankAchCode}`
                    : ''
                }
                onChange={(event) => {
                  const currentCustomerBankAccountsData: Array<BankAccount> =
                    Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                      ? [...(customerData?.bankAccountsData || [])]
                      : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                  currentCustomerBankAccountsData[0].bankName = event.target.value.split('-')[0]
                  currentCustomerBankAccountsData[0].bankAchCode = event.target.value.split('-')[1]

                  const customerDataToSet = {
                    ...customerData,
                    bankAccountsData: [currentCustomerBankAccountsData[0]]
                  }

                  handleSetCustomerData(customerDataToSet)
                }}
              >
                <MenuItem
                  disabled
                  value=""
                >
                  {t('newCustomerModal.customerDataForm.fields.select')}
                </MenuItem>
                {banksData.map((bankData: Bank) => {
                  return (
                    <MenuItem
                      key={bankData.name}
                      value={`${bankData.name}-${bankData.achCode}`}
                    >
                      {bankData.name.toUpperCase()}
                    </MenuItem>
                  )
                })}
              </TextField>
            </div>
          )}
        </div>
        <div className="customers-add-new-form-personal-data-form-row">
          <div className="customers-add-new-form-personal-data-form-field-container">
            <TextField
              fullWidth
              variant="standard"
              sx={textFieldWhiteBg}
              label={showBankNameSelect ? t('newCustomerModal.customerDataForm.fields.bankAccountNumber') : t('newCustomerModal.customerDataForm.fields.keyNumber')}
              error={!!errors.bankAccountNumber}
              helperText={errors.bankAccountNumber}
              value={Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0 ? customerData?.bankAccountsData[0].bankNumber : ''}
              onChange={(event) => {
                if (showBankNameSelect) {
                  if (Number.isNaN(Number(event.target.value))) return

                  const currentCustomerBankAccountsData: Array<BankAccount> =
                    Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                      ? [...(customerData?.bankAccountsData || [])]
                      : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]
                  currentCustomerBankAccountsData[0].bankNumber = event.target.value

                  const customerDataToSet = {
                    ...customerData,
                    bankAccountsData: [currentCustomerBankAccountsData[0]]
                  }

                  handleSetCustomerData(customerDataToSet)
                  return
                }

                const valueToSet = handleAccountNumberChange(event)
                const currentCustomerBankAccountsData: Array<BankAccount> =
                  Array.isArray(customerData?.bankAccountsData) && customerData?.bankAccountsData.length > 0
                    ? [...(customerData?.bankAccountsData || [])]
                    : [{ bankAccountType: '', bankName: '', bankNumber: '', bankAchCode: '', bankAccountMain: true }]

                currentCustomerBankAccountsData[0].bankNumber = valueToSet ? String(valueToSet) : ''
                const customerDataToSet = {
                  ...customerData,
                  bankAccountsData: [currentCustomerBankAccountsData[0]]
                }

                handleSetCustomerData(customerDataToSet)
              }}
            />
          </div>
        </div>
        {handleSetPaymentAmountToSend && (
          <div className="customers-add-new-form-personal-data-form-row">
            <div className="customers-add-new-form-personal-data-form-field-container">
              <TextField
                fullWidth
                variant="standard"
                sx={textFieldWhiteBg}
                value={amountToSend}
                error={!!errors?.amountToSend}
                helperText={errors?.amountToSend}
                label={`${t('paymentForm.amountToSend', { ns: 'individual-money-transfers-management' })} (${currency})`}
                onBlur={(event) => {
                  if (event.target.value === '') {
                    handleSetPaymentAmountToSend(`$ ${numberWithCommas(Number(amountToSend))}`)
                  }
                }}
                onChange={(event) => {
                  const extractedNumber = event.target.value.startsWith('$') ? Number(event.target.value.slice(1).trim().replaceAll(',', '')) : Number(event.target.value.trim().replaceAll(',', ''))

                  if (Number.isNaN(extractedNumber)) {
                    return
                  }

                  if (event.target.value.trim() === '$' || event.target.value.trim() === '0') {
                    return handleSetPaymentAmountToSend('')
                  }

                  const valueToSet = handleGetParsedValue(event)
                  handleSetPaymentAmountToSend(valueToSet)
                }}
              />
            </div>
          </div>
        )}
        {userSession?.role === 'super-admin-client' && location.pathname === '/' && Number(currentPocketsData?.length) > 0 && (
          <div className="customers-add-new-form-personal-data-form-row">
            <TextField
              select
              fullWidth
              variant="standard"
              value={selectedPocketId || ''}
              label={t('paymentForm.fundsFrom', { ns: 'individual-money-transfers-management' })}
              error={!!errors?.selectedPocketId}
              helperText={errors?.selectedPocketId}
              onChange={(event) => handleSetPocketId?.(event.target.value)}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('paymentForm.select', { ns: 'individual-money-transfers-management' })}
              </MenuItem>
              {currentPocketsData?.map((pocketData: Pocket) => {
                return (
                  <MenuItem
                    key={String(pocketData.id)}
                    value={String(pocketData.id)}
                  >
                    {`${pocketData.name?.toUpperCase()} - $ ${numberWithCommas(Number(pocketData.balance))}`}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
      </div>
      {/* errors.generalError && <div className="error-message">{errors.generalError}</div> */}
    </div>
  )
}

export default CustomerDataForm
